import {
    FlowBlock, FlowPort, FlowWire,
    SimulationStatus
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IPanelDatum, PanelTitleFn} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {IVisualDataStreamer} from '@mintdata-wsp/wsp/controllers/visualDesigner/visualData/VisualDataStreamer';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PanelContentProps} from '@mintdata-wsp/wsp/ui/views/visualDesigner/general/layout/PanelView';
import {DebugBlockView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/debugData/block/DebugBlockView';
import {
    DebugDataMessageView,
    DebugDataState
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/debugData/DebugDataMessageView';
import {DebugWireView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/debugData/wire/DebugWireView';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./DebugDataPanel.less');
const cx = classnames.bind(styles);

export enum DebugKind
{
    Block = 'block',
    Wire = 'wire'
}

export class DebugBlockContext implements IPanelDatum
{
    public readonly datumType = 'DebugBlockContext';

    constructor(public readonly block:FlowBlock,
                public readonly inputPort:FlowPort | null,
                public readonly outputPort:FlowPort | null,
                public readonly inputStreamer:IVisualDataStreamer,
                public readonly outputStreamer:IVisualDataStreamer)
    {
    }

    isEqualTo(other:IPanelDatum):boolean
    {
        if( !other )
        {
            return false;
        }

        const otherCtx = other as DebugBlockContext;
        return otherCtx.datumType === this.datumType && otherCtx.block && otherCtx.block.isEqualTo(this.block);
    }
}

export class DebugWireContext implements IPanelDatum
{
    public readonly datumType = 'DebugWireContext';

    constructor(public readonly wire:FlowWire,
                public readonly streamer:IVisualDataStreamer)
    {
    }

    isEqualTo(other:IPanelDatum):boolean
    {
        if( !other )
        {
            return false;
        }

        const otherCtx = other as DebugWireContext;
        return otherCtx.datumType === this.datumType && otherCtx.wire && otherCtx.wire.isEqualTo(this.wire);
    }
}

export interface DebugDataPanelProps extends PanelContentProps
{
    kind:DebugKind;
    panelTitle:PanelTitleFn;
    context:DebugBlockContext | DebugWireContext;
}

@observer
export class DebugDataPanel extends React.Component<DebugDataPanelProps, any>
{
    private _renderDebugUI()
    {
        const {kind, context} = this.props;

        if( kind === DebugKind.Wire )
        {
            const wireCtx = context as DebugWireContext;

            return (
                <DebugWireView
                    panelTitleFn={this.props.panelTitle}
                    {...this.props}
                    context={wireCtx}
                />
            );
        }
        else if( kind === DebugKind.Block )
        {
            const blockCtx = context as DebugBlockContext;

            return <DebugBlockView {...this.props} context={blockCtx} />;
        }
        else
        {
            return null;
        }
    }

    private _areStreamersEmpty():boolean
    {
        const {kind, context} = this.props;

        if( kind === DebugKind.Block )
        {
            const ctx = context as DebugBlockContext;
            return ctx.inputStreamer.isEmpty && ctx.outputStreamer.isEmpty;
        }
        else if( kind === DebugKind.Wire )
        {
            const ctx = context as DebugWireContext;
            return ctx.streamer.isEmpty;
        }
        else
        {
            return true;
        }
    }

    private _computeDebugDataState():DebugDataState
    {
        const {flow, flowSimulation} = controllers.ui;

        const openFlow = flow.getOpenFlow();

        const simulation = flowSimulation.getForFlowEditor(openFlow.id);
        const status = flowSimulation.getSimulationStatus(simulation.id);

        switch( status )
        {
            case SimulationStatus.InitialNotRunning:
                return DebugDataState.DesignNeverStarted;
            case SimulationStatus.Running:
            {
                if( this._areStreamersEmpty() )
                {
                    return DebugDataState.NoDataForCurrentBlockOrWire;
                }
                else
                {
                    return DebugDataState.VisualDataExists;
                }

            }
            case SimulationStatus.Starting:
            case SimulationStatus.WaitingForFirstVisualData:
                return DebugDataState.DesignStartedWaitingForData;
            case SimulationStatus.Stopped:
            {
                if( flow.hasFlowErrors(openFlow.id) || flow.hasCloudFlowErrors(openFlow.id) )
                {
                    return DebugDataState.Stopped_FlowErrorsExist;
                }
                else
                {
                    if( flowSimulation.isEverHadData(simulation.id) )
                    {
                        return DebugDataState.Stopped_StaleVisualDataExists;
                    }
                    else
                    {
                        return DebugDataState.DesignNeverStarted;
                    }
                }
            }
        }
    }

    render()
    {
        const {flow, flowSimulation} = controllers.ui;
        const {kind, panelTitle} = this.props;

        const openFlow = flow.getOpenFlow();

        if( !openFlow )
        {
            return null;
        }

        const simulation = flowSimulation.getForFlowEditor(openFlow.id);

        if (!simulation)
        {
            return null;
        }

        const debugState = this._computeDebugDataState();

        if( debugState === DebugDataState.VisualDataExists ||
            debugState === DebugDataState.Stopped_StaleVisualDataExists )
        {
            return this._renderDebugUI();
        }
        else
        {
            return (
                <DebugDataMessageView
                    kind={kind}
                    panelTitle={panelTitle}
                    state={debugState}
                />
            );
        }
    }
}
